import { useState } from "react";
import { formatDistanceToNow, parseISO } from "date-fns";
import { ChevronRight, Repeat } from "lucide-react";
import { Button } from "@/modern/components/ui/button-shadcn";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { cn } from "@/modern/utils";
import type { CheckGridOverviewItem } from "./types";
import { buildOverviewLanes, attentionHeadline, overviewSignal, recurrenceLabel, satisfiedFraction, signalMeta } from "./utils/display";
import { CheckgridMenu, type CheckgridMenuAction } from "./CheckgridMenu";
import { CheckgridEntityLink } from "./CheckgridEntityLink";
import { CheckgridProgressMini } from "./CheckgridProgressMini";
import { CheckgridsOverviewSkeleton } from "./CheckgridsSkeletons";

const LANE_PAGE_SIZE = 10;

type Props = {
  grids: CheckGridOverviewItem[];
  onSelect: (id: number) => void;
  onAction: (action: CheckgridMenuAction, grid: CheckGridOverviewItem, extras?: { file?: File }) => void;
  isFiltered?: boolean;
  isLoading?: boolean;
};

export function CheckgridsOverview({ grids, onSelect, onAction, isFiltered, isLoading }: Props) {
  const [shownByLane, setShownByLane] = useState<Record<string, number>>({});
  const lanes = buildOverviewLanes(grids);
  const totals = grids.reduce(
    (acc, grid) => {
      const { complete, applicable } = satisfiedFraction(grid);
      return { complete: acc.complete + complete, applicable: acc.applicable + applicable };
    },
    { complete: 0, applicable: 0 }
  );
  return (
    <div className="flex h-full min-h-0 min-w-0 flex-col bg-background">
      <header className="flex shrink-0 items-end justify-between gap-6 border-b border-border px-10 py-5">
        <div className="min-w-0">
          <h1 className="text-[23px] tracking-tight text-foreground">Right now</h1>
          <p className="mt-1 text-[13.5px] text-muted-foreground">
            {totals.applicable > 0 ? (
              <>
                <span className="text-foreground tabular-nums">
                  {totals.complete} of {totals.applicable}
                </span>{" "}
                checks done across {grids.length} checklist{grids.length === 1 ? "" : "s"}
              </>
            ) : (
              "Board checklists — who still needs to finish what."
            )}
          </p>
        </div>
      </header>

      <div className="min-h-0 min-w-0 flex-1 overflow-auto px-10 py-7">
        {isLoading ? (
          <CheckgridsOverviewSkeleton />
        ) : grids.length === 0 ? (
          <div className="rounded-lg border border-border bg-card px-5 py-6">
            <h2 className="text-[16px] font-medium tracking-tight text-foreground">{isFiltered ? "No checklists match these filters" : "Nothing in progress yet"}</h2>
            <p className="mt-1 text-[13.5px] text-muted-foreground">{isFiltered ? "Try a different search or clear some filters." : "Checklists appear here once their first item is checked."}</p>
          </div>
        ) : (
          lanes.map((lane) => {
            const shown = shownByLane[lane.key] ?? LANE_PAGE_SIZE;
            const remaining = lane.grids.length - shown;
            return (
              <section key={lane.key} className="mb-7 min-w-0">
                <div className="mb-2 flex items-center gap-2">
                  <h2 className="text-[12.5px] font-medium tracking-[0.09em] text-muted-foreground uppercase">{lane.title}</h2>
                  <span className="font-mono text-[11.5px] text-muted-foreground/60">{lane.grids.length}</span>
                </div>

                <div className="overflow-x-auto rounded-lg border border-border bg-card">
                  <div className="min-w-[720px] divide-y divide-border/70">
                    {lane.grids.slice(0, shown).map((grid) => {
                      const signal = overviewSignal(grid);
                      const { last_activity } = grid.summary;
                      const activity = last_activity ? `Last updated ${formatDistanceToNow(parseISO(last_activity), { addSuffix: true })}` : null;
                      return (
                        <div
                          key={grid.id}
                          role="button"
                          tabIndex={0}
                          onClick={() => onSelect(grid.id)}
                          onKeyDown={(e) => {
                            if (e.target === e.currentTarget && (e.key === "Enter" || e.key === " ")) {
                              e.preventDefault();
                              onSelect(grid.id);
                            }
                          }}
                          className="group flex cursor-pointer items-center gap-4 px-4 py-3 transition-colors hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
                        >
                          <div className="w-[280px] shrink-0">
                            <div className="flex min-w-0 items-center gap-1.5">
                              <span className="truncate text-[14.5px] font-medium text-foreground">{grid.name}</span>
                              {grid.recurrence !== "none" && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <span className="inline-flex shrink-0 text-muted-foreground/70" aria-label={recurrenceLabel(grid.recurrence)}>
                                      <Repeat className="size-3.5" aria-hidden />
                                    </span>
                                  </TooltipTrigger>
                                  <TooltipContent side="bottom" className="text-xs">
                                    {recurrenceLabel(grid.recurrence)}
                                  </TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            <CheckgridEntityLink grid={grid} onAction={onAction} className="mt-0.5 text-[12px] text-muted-foreground" />
                          </div>

                          <div className="flex min-w-[200px] flex-1 flex-col gap-0.5 text-[13px]">
                            {signal === "attention" && (
                              <span className={cn("flex items-center gap-1.5", signalMeta(signal).text)}>
                                <span className={cn("size-1.5 shrink-0 rounded-full", signalMeta(signal).dot)} aria-hidden />
                                {attentionHeadline(grid)}
                              </span>
                            )}
                            {activity && satisfiedFraction(grid).applicable > 0 && <span className="text-[12.5px] text-muted-foreground">{activity}</span>}
                          </div>

                          <CheckgridProgressMini grid={grid} />

                          <div className="flex shrink-0 items-center gap-1">
                            <div className="opacity-0 transition-opacity group-hover:opacity-100 focus-within:opacity-100" onClick={(e) => e.stopPropagation()}>
                              <CheckgridMenu grid={grid} onAction={onAction} />
                            </div>
                            <ChevronRight className="size-4 text-muted-foreground/50" aria-hidden />
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
                {remaining > 0 && (
                  <Button type="button" variant="link" size="sm" className="mt-1 px-1" onClick={() => setShownByLane((prev) => ({ ...prev, [lane.key]: shown + LANE_PAGE_SIZE }))}>
                    View more ({remaining} more)
                  </Button>
                )}
              </section>
            );
          })
        )}
      </div>
    </div>
  );
}
